import { useCallback, useEffect, useRef, useState } from 'react'

import {
  beaconReleaseReviewTask,
  claimReviewTask,
  heartbeatReviewTask,
  releaseReviewTask,
} from '../../../api/reviewTasks'

const HEARTBEAT_MS = 30 * 1000
const TOKEN_PREFIX = 'didge-review-claim:'

const tokenKey = (projectId: string, taskId: string) =>
  `${TOKEN_PREFIX}${projectId}:${taskId}`

export const useReviewTaskClaim = (
  projectId: string,
  taskId: string | undefined,
  options: { disabled?: boolean } = {},
) => {
  const disabled = options.disabled ?? false
  const [claimToken, setClaimToken] = useState<string | null>(null)
  const [activeSeconds, setActiveSeconds] = useState(0)
  const tokenRef = useRef<string | null>(null)
  const secondsRef = useRef<(() => number) | null>(null)
  const releasedRef = useRef(false)

  const setSecondsProvider = useCallback((getter: () => number) => {
    secondsRef.current = getter
  }, [])

  const releaseClaim = useCallback(
    (beacon: boolean) => {
      if (releasedRef.current) {
        return
      }
      const token = tokenRef.current
      if (!token || !projectId || !taskId) {
        return
      }
      releasedRef.current = true
      tokenRef.current = null
      const seconds = secondsRef.current?.() ?? 0
      const payload = { claim_token: token, accumulated_seconds: seconds }
      if (beacon) {
        beaconReleaseReviewTask(projectId, taskId, payload)
        return
      }
      void releaseReviewTask(projectId, taskId, payload).catch(() => undefined)
    },
    [projectId, taskId],
  )

  useEffect(() => {
    if (!projectId || !taskId || disabled) {
      return undefined
    }
    let cancelled = false
    releasedRef.current = false

    const run = async () => {
      try {
        const result = await claimReviewTask(projectId, taskId)
        if (cancelled) {
          await releaseReviewTask(projectId, taskId, {
            claim_token: result.claim_token,
            accumulated_seconds: result.active_seconds,
          })
          return
        }
        tokenRef.current = result.claim_token
        setClaimToken(result.claim_token)
        setActiveSeconds(result.active_seconds || 0)
        try {
          sessionStorage.setItem(tokenKey(projectId, taskId), result.claim_token)
        } catch {
          /* ignore */
        }
      } catch {
        /* keep working; heartbeat will retry a token */
      }
    }
    void run()

    return () => {
      cancelled = true
      releaseClaim(false)
    }
  }, [disabled, projectId, releaseClaim, taskId])

  useEffect(() => {
    if (!claimToken || !projectId || !taskId || disabled) {
      return undefined
    }
    const id = window.setInterval(() => {
      const seconds = secondsRef.current?.() ?? 0
      void heartbeatReviewTask(projectId, taskId, {
        claim_token: claimToken,
        accumulated_seconds: seconds,
      })
        .then(result => {
          if (result.claim_token && result.claim_token !== tokenRef.current) {
            tokenRef.current = result.claim_token
            setClaimToken(result.claim_token)
          }
        })
        .catch(() => undefined)
    }, HEARTBEAT_MS)
    return () => window.clearInterval(id)
  }, [claimToken, disabled, projectId, taskId])

  useEffect(() => {
    if (!claimToken || !projectId || !taskId || disabled) {
      return undefined
    }
    const onUnload = () => releaseClaim(true)
    window.addEventListener('pagehide', onUnload)
    window.addEventListener('beforeunload', onUnload)
    return () => {
      window.removeEventListener('pagehide', onUnload)
      window.removeEventListener('beforeunload', onUnload)
    }
  }, [claimToken, disabled, projectId, releaseClaim, taskId])

  return {
    claimToken,
    claimedActiveSeconds: activeSeconds,
    setSecondsProvider,
  }
}
